/* eslint-disable no-console */
import axios from 'axios';
import { VueCookieNext } from 'vue-cookie-next';
import admin from './lib/admin';
import guest from './lib/guest';
import customer from './lib/customer';
import { ADMIN_TYPE, CUSTOMER_TYPE } from './types';
import { magentoOptions, isUserCMallOnlineUrl } from '../../config/magento';
import { getObjectValue } from '@/functions/string/getObjectValue';
import { Toast } from 'vant';
import { notifyImageUploadAuditTip } from '@/functions/apis-pages/handleImage';

/** HTTP 500 时交给业务层展示（如 Toast.fail），与 getApiDatas catch 约定一致 */
const HTTP_500_API_MESSAGE = '网络开小差了  请稍后重试～';
const LOGIN_REQUIRED_MSG = '用户ID不能为空';
/** 跳转登录前写入的完整路由（localStorage） */
const LOGIN_REDIRECT_ROUTE_KEY = 'LOGIN_REDIRECT_ROUTE';
/** cookie 丢失时用 localStorage 恢复（小程序 web-view 重开常见） */
function getLoginBackupStorageKey() {
  return 'UserCustomerLoginBackup-' + (isUserCMallOnlineUrl ? 1 : 0);
}
/** 防止多个接口同时返回 -444 时短时间内重复处理 */
let lastLoginRedirectAt = 0;
const LOGIN_REDIRECT_DEBOUNCE_MS = 2000;
/** 服务端会话探测冷却，避免回前台/多入口频繁打 getSelfInfo */
let lastSessionCheckAt = 0;
let sessionCheckPromise = null;
const SESSION_CHECK_COOLDOWN_MS = 60 * 1000;

function resetSessionCheckCooldown() {
  lastSessionCheckAt = 0;
}

/** 未登录 / 用户 ID 缺失 / 异地登录导致会话失效 */
function shouldRedirectToLogin(payload) {
  if (!payload || typeof payload !== 'object') {
    return false;
  }
  const code = getObjectValue(payload, 'code', null);
  const msg = String(getObjectValue(payload, 'msg', '')).trim();
  return code == -444 || msg === LOGIN_REQUIRED_MSG;
}

/** 跳转登录前保存当前完整路由，供登录成功后回跳 */
export function saveLoginRedirectRoute() {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return;
  }
  try {
    const { location } = window;
    const pathname = String(location.pathname || '');
    // 已在登录页时不再覆盖，避免回跳指向 /login
    if (pathname.endsWith('/login') || pathname.includes('/login')) {
      return;
    }
    const fullPath = `${pathname}${location.search || ''}${location.hash || ''}`;
    localStorage.setItem(
      LOGIN_REDIRECT_ROUTE_KEY,
      JSON.stringify({
        href: location.href || '',
        origin: location.origin || '',
        pathname,
        search: location.search || '',
        hash: location.hash || '',
        fullPath,
      }),
    );
  } catch (e) {
    console.log('saveLoginRedirectRoute err: ', e);
  }
}

/**
 * 主动跳转登录页前写入回跳地址（公开页如 AiQA 需自行调用）
 * @param {import('vue-router').Router} router
 */
export function redirectToLogin(router) {
  saveLoginRedirectRoute();
  return import('@/functions/apis-pages/wechatSDKJs.js')
    .then(({ isWechatMiniProgramSync, gotoWechatLogin }) => {
      if (isWechatMiniProgramSync()) {
        // 不要 reset 锁：真机多接口会把已打开的授权页再叠一层
        gotoWechatLogin();
        return undefined;
      }
      if (router && typeof router.push === 'function') {
        return router.push({ name: 'login' });
      }
      const base = (process.env.BASE_URL || '/').replace(/\/$/, '');
      window.location.href = `${base}/login`;
      return undefined;
    })
    .catch(() => {
      if (router && typeof router.push === 'function') {
        return router.push({ name: 'login' });
      }
      const base = (process.env.BASE_URL || '/').replace(/\/$/, '');
      window.location.href = `${base}/login`;
      return undefined;
    });
}

/**
 * 登录成功后的回跳路径：优先 LOGIN_REDIRECT_ROUTE，否则由调用方提供 fallback
 * @param {string} [fallback='/']
 * @returns {string}
 */
export function resolvePostLoginRedirectPath(fallback = '/') {
  const saved = consumeLoginRedirectRoute();
  if (saved && saved !== '/login' && !saved.endsWith('/login')) {
    return saved;
  }
  const fb = String(fallback || '').trim();
  return fb || '/';
}

/**
 * 当前路由是否必须登录才能停留。
 * 与 router.beforeEach 一致：仅 meta.requireAuth === true 时强制进登录页。
 * 公开页（推荐官 H5 等无 requireAuth）只清本地态，不整页跳转。
 */
function currentRouteRequiresAuth(route) {
  if (!route || typeof route !== 'object') {
    return false;
  }
  const name = getObjectValue(route, 'name', '');
  if (name === 'login' || name === 'testDon') {
    return false;
  }
  return getObjectValue(route, 'meta', 'requireAuth', undefined) === true;
}

/**
 * 会话失效统一处理：
 * 1. 始终清除本地登录 cookie
 * 2. 小程序：拉授权（有打开锁，不会叠页）；从授权返回后可再拉
 * 3. H5：仅「必须登录」路由跳 /login
 */
function handleSessionExpired() {
  if (typeof window === 'undefined') {
    return;
  }
  const now = Date.now();
  // 短防抖合并并发；是否再拉授权由 gotoWechatLogin 打开锁决定
  if (now - lastLoginRedirectAt < LOGIN_REDIRECT_DEBOUNCE_MS) {
    return;
  }
  lastLoginRedirectAt = now;

  // 先清本地态，避免后续逻辑继续把失效 cookie 当成已登录
  magento.logout();
  resetSessionCheckCooldown();

  Promise.all([
    import('@/router'),
    import('@/functions/apis-pages/wechatSDKJs.js'),
  ])
    .then(([{ default: router }, { isWechatMiniProgramSync, gotoWechatLogin }]) => {
      const route = router.currentRoute && router.currentRoute.value;
      if (route && route.name === 'login') {
        if (isWechatMiniProgramSync()) {
          gotoWechatLogin();
        }
        return;
      }
      // 小程序：出现需登录即尝试拉授权（内部有打开锁，不会重复叠页）
      if (isWechatMiniProgramSync()) {
        gotoWechatLogin();
        return;
      }
      // H5：仅必须登录页跳 /login
      if (!currentRouteRequiresAuth(route)) {
        return;
      }
      saveLoginRedirectRoute();
      return router.push({ name: 'login' });
    })
    .catch((e) => {
      console.log('handleSessionExpired router err: ', e);
      // 拿不到路由时保守跳登录，避免需登录页卡死
      const base = (process.env.BASE_URL || '/').replace(/\/$/, '');
      const path = (window.location.pathname || '');
      if (!path.endsWith('/login') && !path.includes('/login')) {
        saveLoginRedirectRoute();
        window.location.href = `${base}/login`;
      }
    });
}

/** 统一处理需登录的接口响应（清本地态；是否跳登录见 currentRouteRequiresAuth） */
function handleLoginRequiredResponse(payload) {
  if (!shouldRedirectToLogin(payload)) {
    return false;
  }
  handleSessionExpired();
  return true;
}

/** 读取跳转登录前保存的路由；登录成功后可用来回跳 */
export function getLoginRedirectRoute() {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  try {
    const raw = localStorage.getItem(LOGIN_REDIRECT_ROUTE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (e) {
    console.log('getLoginRedirectRoute err: ', e);
    return null;
  }
}

export function clearLoginRedirectRoute() {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.removeItem(LOGIN_REDIRECT_ROUTE_KEY);
  } catch (e) {
    console.log('clearLoginRedirectRoute err: ', e);
  }
}

/** 解析为 vue-router 可用的 path（含 query/hash），并清除 localStorage 缓存 */
export function consumeLoginRedirectRoute() {
  const saved = getLoginRedirectRoute();
  if (!saved) {
    return '';
  }
  clearLoginRedirectRoute();
  const fullPath = String(getObjectValue(saved, 'fullPath', '')).trim();
  if (fullPath) {
    return toRouterPath(fullPath);
  }
  const href = String(getObjectValue(saved, 'href', '')).trim();
  if (href) {
    return toRouterPath(href);
  }
  return '';
}

function toRouterPath(pathOrUrl) {
  const s = String(pathOrUrl || '').trim();
  if (!s) {
    return '';
  }
  if (/^https?:\/\//i.test(s)) {
    try {
      const u = new URL(s);
      return u.pathname + u.search + u.hash;
    } catch (e) {
      console.log('toRouterPath err: ', e);
      return '';
    }
  }
  return s.startsWith('/') ? s : `/${s}`;
}

export const cookie = VueCookieNext;
export function isNumber(n) {
  return typeof n === 'number' && !Number.isNaN(n) && Number.isFinite(n);
}

export function translate() {
  
}

const defaultOptions = {
  url: null,
  userAgent: 'YuSheng Magento Library',
  home_cms_block_id: '',
  authentication: {
    integration: {
      access_token: undefined,
    },
  },
};

class Magento {
  init(options) {
    this.configuration = { ...defaultOptions, ...options };
    this.getCustomerToken();
    this.base_url = this.configuration.url.replace(
      /[\\/]{1,}$/, ''
    );
    this.root_path = `/${this.configuration.store}`;
    this.admin = admin(this);
    this.guest = guest(this);
    this.customer = customer(this);
    if (this.configuration.authentication.integration.access_token) {
      this.access_token = this.configuration.authentication.integration.access_token;
    } else {
      throw integrationTokenError();
    }
  }

  post(path, params, data, type = ADMIN_TYPE) {
    return this.send(path, 'POST', params, data, type);
  }

  put(path, params, data, type = ADMIN_TYPE) {
    return this.send(path, 'PUT', params, data, type);
  }

  get(path, params, data, type = ADMIN_TYPE) {
    return this.send(path, 'GET', params, data, type);
  }

  delete(path, params, data, type = ADMIN_TYPE) {
    return this.send(path, 'DELETE', params, data, type);
  }

  send(path, method, params, data, type) {
    let url = `${this.base_url}${this.root_path}${path}`;
    if (!isUserCMallOnlineUrl) {
      url = `/${this.root_path}${path}`.replace(
        /^[\\/]{1,}/, '/'
      );
    }
    console .log('this.base_url', url)
    
    
    const headers = {
      // 'User-Agent': this.configuration.userAgent,
      'Content-Type': 'application/json',
    };

    this.customerToken = this.getCustomerToken();
    if (this.access_token && type === ADMIN_TYPE) {
      headers.Authorization = `Bearer ${this.access_token}`;
    } else if (typeof this.customerToken == 'string' && this.customerToken.trim() != '' && type === CUSTOMER_TYPE) {
      headers.Authorization = `Bearer ${this.customerToken}`;
    }

    return new Promise((resolve, reject) => {
      axios({
        url,
        method,
        headers,
        params,
        data,
        withCredentials: false
      })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          if (error.response) {
            const responseData = error.response.data;
            if (shouldRedirectToLogin(responseData)) {
              reject(
                typeof responseData === 'object' && responseData !== null
                  ? responseData
                  : { code: -444, msg: LOGIN_REQUIRED_MSG },
              );
              return;
            }
            if (error.response.status === 500) {
              reject({ code: 500, msg: HTTP_500_API_MESSAGE });
              return;
            }
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            if (error.response.status === 404 && error.response.data == null) {
              reject();
              return;
            }
          } else if (error.request) {
            // The request was made but no response was received
            // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
            // http.ClientRequest in node.js
            // const noReponseError = noResponseFromServerError();
            reject({ code: 500, msg: HTTP_500_API_MESSAGE });
            return;
          } else {
            reject({ code: 500, msg: HTTP_500_API_MESSAGE });
            return;
            // Something happened in setting up the request that triggered an Error
          }
          let customError;
          if (
            typeof error.response.data === 'object' &&
            error.response.data !== null
          ) {
            customError = Magento.extractErrorMessage(error.response.data);
          } else {
            customError = pageNotFoundError();
          }
          if (typeof customError == 'object' && null != customError) {
            customError.responseData = error.response.data;
          }
          if (
            typeof customError.responseData == 'object' && 
            null != customError.responseData &&
            typeof customError.responseData.code == 'number'
          ) {
            resolve(customError.responseData);
            return;
          }
          reject(customError);
        });
    });
  }

  static extractErrorMessage(data) {
    const { parameters } = data;
    let { message } = data;

    if (parameters && Array.isArray(parameters) && parameters.length > 0) {
      parameters.forEach((item, index) => {
        message = message.replace(`%${index + 1}`, item);
      });
    } else if (parameters && parameters instanceof Object) {
      Object.keys(parameters).forEach(parameter => {
        message = message.replace(`%${parameter}`, parameters[parameter]);
      });
    }

    return { message };
  }

  isConfigured() {
    return this.access_token != null;
  }

  setStoreConfig(config) {
    this.storeConfig = config;
  }

  isLogin() {
    const userInfo = getUserInfo();
    return null == userInfo ? false : true;
  }


  logout() {
    cookie.removeCookie(
      'UserCustomerLoginInfo-'+(isUserCMallOnlineUrl?1:0)
    );
    clearLoginBackup();
  }

  getCustomerToken() {
    let token = this.customerToken;
    if ((
      typeof token != 'string' && typeof token != 'number'
    ) || '' == (token+'').trim()) {
      token = cookie.getCookie(
        'UserCustomerToken-'+(isUserCMallOnlineUrl?1:0)
      );
    }
    if ((
      typeof token != 'string' && typeof token != 'number'
    ) || '' == (token+'').trim()) {
      token = '';
    }
    token = (token+'').trim();
    this.customerToken = token;
    return token;
  }

  setCustomerToken(token) {
    if ((
      typeof token != 'string' && typeof token != 'number'
    ) || '' == (token+'').trim()) {
      return;
    }
    this.customerToken = token;
    cookie.setCookie(
      'UserCustomerToken-'+(isUserCMallOnlineUrl?1:0), 
      token, { expire: 60 * 60 * 24 * 1 }
    );
  }

  getBaseUrl() {
    return this.base_url || '';
  }

  getMediaUrl() {
    if (
      typeof this.base_url == 'string' && 
      (
        typeof this.storeConfig != 'object' || null == this.storeConfig || 
        typeof this.storeConfig.base_media_url != 'string'
      )
    ) {
      return this.base_url+'media/';
    }
    return `${this.storeConfig.base_media_url}`;
  }

  getProductMediaUrl() {
    return this.getMediaUrl()+'catalog/product';
  }

  getHomeData() {
    if (isNumber(this.configuration.home_cms_block_id)) {
      return this.admin.getCmsBlock(this.configuration.home_cms_block_id);
    }
    throw homeCmsBlockError();
  }
}

function homeCmsBlockError() {
  const name = translate('errors.homeCmsBlockId');
  const message = translate('errors.homeCmsBlockIdMessage');
  return createError(name, message);
}

function integrationTokenError() {
  const name = translate('errors.integrationTokenRequired');
  const message = translate('errors.integrationTokenRequiredMessage');
  return createError(name, message);
}

// function noResponseFromServerError() {
//   const name = translate('errors.noResponseFromServer');
//   const message = translate('errors.noResponseFromServerMessage');
//   return createError(name, message);
// }

function pageNotFoundError() {
  const name = translate('errors.404error');
  const message = translate('errors.404errorMessage');
  return createError(name, message);
}

function createError(name, message) {
  const error = new Error();
  error.name = name;
  error.message = message;
  return error;
}

// Constants
export const CUSTOMER_TOKEN = 'customerToken';
export const CURRENCY_CODE = 'currencyCode';

export const magento = new Magento();
magento.init(magentoOptions);

function isTureUserObject(userInfo) {
  if (
    typeof userInfo != 'object' || null == userInfo ||
    (typeof userInfo.uid != 'string' && typeof userInfo.uid != 'number') ||
    '' === (userInfo.uid+'').trim() || 
    (typeof userInfo.token != 'string' && typeof userInfo.token != 'number') ||
    '' === (userInfo.token+'').trim()
  ) {
    return false;
  }
  return true;
}

function backupLoginInfo(uid, token) {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(
      getLoginBackupStorageKey(),
      JSON.stringify({ uid, token, t: Date.now() }),
    );
  } catch (e) {
    console.log('backupLoginInfo err: ', e);
  }
}

function clearLoginBackup() {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.removeItem(getLoginBackupStorageKey());
  } catch (e) {
    console.log('clearLoginBackup err: ', e);
  }
}

/**
 * cookie 缺失时从 localStorage 写回 cookie（不校验服务端，交给后续接口 / ensureServerSession）
 * @returns {{uid: *, token: *}|null}
 */
function restoreLoginFromBackup() {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  try {
    const raw = localStorage.getItem(getLoginBackupStorageKey());
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    const userInfo = {
      uid: parsed && parsed.uid,
      token: parsed && parsed.token,
    };
    if (!isTureUserObject(userInfo)) {
      clearLoginBackup();
      return null;
    }
    cookie.setCookie(
      'UserCustomerLoginInfo-' + (isUserCMallOnlineUrl ? 1 : 0),
      JSON.stringify(userInfo),
      { expire: 60 * 60 * 24 * 30 },
    );
    return userInfo;
  } catch (e) {
    console.log('restoreLoginFromBackup err: ', e);
    return null;
  }
}

/**
 * 得到用户的登录信息（仅本地 cookie，不代表服务端会话仍有效）
 * cookie 丢失时尝试从 localStorage 备份恢复
 */
export function getUserInfo() {
  try {
    let userInfo = cookie.getCookie(
      'UserCustomerLoginInfo-'+(isUserCMallOnlineUrl?1:0)
    );
    if (isTureUserObject(userInfo)) {
      return userInfo;
    }
    // vue-cookie-next 对 {...} 会自动 parse；未解析到的字符串再手动 parse
    if (typeof userInfo === 'string' && userInfo.trim() !== '') {
      try {
        userInfo = JSON.parse(userInfo);
        if (isTureUserObject(userInfo)) {
          return userInfo;
        }
      } catch (e) {
        /* fall through to backup */
      }
    }
    return restoreLoginFromBackup();
  } catch (e) {
    console.log('从cookie里无法得到登录信息: ', e);
  }
  try {
    return restoreLoginFromBackup();
  } catch (e2) {
    console.log('restoreLoginFromBackup err: ', e2);
    return null;
  }
}

/**
 * 向服务端确认本地登录态是否仍有效。
 * - isLogin()/getUserInfo() 保持同步、只读本地
 * - 失效时走既有 handleLoginRequiredResponse（清 cookie，需登录页再跳登录）
 * - 网络异常不踢登录，避免弱网误伤
 * @param {{ force?: boolean }} options force=true 时忽略冷却（关键操作前用）
 * @returns {Promise<boolean>} 当前是否仍视为已登录
 */
export async function ensureServerSession(options = {}) {
  if (!magento.isLogin()) {
    return false;
  }
  const force = !!(options && options.force);
  const now = Date.now();
  if (!force && now - lastSessionCheckAt < SESSION_CHECK_COOLDOWN_MS) {
    return magento.isLogin();
  }
  if (sessionCheckPromise) {
    return sessionCheckPromise;
  }
  sessionCheckPromise = (async () => {
    try {
      const apiRes = await getApiDatas('user', 'getSelfInfo', {}, { isShowLoading: false });
      lastSessionCheckAt = Date.now();
      // getApiDatas 内已对「用户ID不能为空」/-444 走 handleSessionExpired：
      // 清本地态；仅 meta.requireAuth 页跳登录，公开页留在原地（不再补跳）
      if (shouldRedirectToLogin(apiRes) || String(getObjectValue(apiRes, 'msg', '')).trim() === LOGIN_REQUIRED_MSG) {
        return false;
      }
      return magento.isLogin();
    } catch (e) {
      // 网络失败不清理本地态
      console.log('ensureServerSession err: ', e);
      return magento.isLogin();
    } finally {
      sessionCheckPromise = null;
    }
  })();
  return sessionCheckPromise;
}

/**
 * App 级：启动探测一次，并在页面从后台回到前台时再探测（受冷却限制）
 * @returns {() => void} 卸载时取消监听
 */
export function bindServerSessionLifecycle() {
  if (typeof document === 'undefined') {
    return () => {};
  }
  // 启动时先尝试从 localStorage 恢复 cookie，并把现有登录写入备份
  const info = getUserInfo();
  if (info) {
    backupLoginInfo(info.uid, info.token);
  }
  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      getUserInfo();
      ensureServerSession();
    }
  };
  ensureServerSession();
  document.addEventListener('visibilitychange', onVisibilityChange);
  return () => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };
}

/**
 * 保存用户的相关登录信息
 */
export function saveUserInfo(uid, userToken, options = {}) {
  if (typeof options != 'object' || null == options) options = {};
  if (typeof options.success != 'function') options.success = () => {};
  if (typeof options.fail != 'function') options.fail = () => {};
  
  if (
    (typeof uid != 'number' && typeof uid != 'string') ||
    '' == (uid+'').trim() || (uid+'').length > 32
  ) {
    options.fail();
    return;
  }
  try {
    const saveData = {uid, token: userToken};
    cookie.setCookie(
      'UserCustomerLoginInfo-'+(isUserCMallOnlineUrl?1:0), 
      JSON.stringify(saveData), { expire: 60 * 60 * 24 * 30 }
    );
    backupLoginInfo(uid, userToken);
    resetSessionCheckCooldown();
    options.success(saveData);
    return;
  } catch (e) {
    console .log('保存登录信息失败', e);
  }
  options.fail();
  return;
}

/**
 * 得到apitoken
 */
function getApiTokenString() {
  const tokenResults = {
          code: -1,
          msg: '错误的token',
          apiToken: ''
        };
  return new Promise((r) => {
    magento.customer.getToken().then(token => {
      if (!(typeof token != 'string' || (token+'').trim() == '')) {
        tokenResults.code = 1;
        tokenResults.apiToken = token;
        tokenResults.msg = '';
        r(tokenResults);
        return tokenResults;
      }
      magento.customer.getToken().then(token => {
        if (!(typeof token != 'string' || (token+'').trim() == '')) {
          tokenResults.code = 1;
          tokenResults.apiToken = token;
          tokenResults.msg = '';
        }
        r(tokenResults);
        return tokenResults;
      });
    });
  });
}

/**
 * 得到API的数据
 */
export async function getApiDatas(apiName, apiAction, bodyData = {}, options = {}) {
  if (typeof bodyData != 'object' || null == bodyData) bodyData = {};
  if (typeof options != 'object' || null == options) options = {};
  if (typeof options.isShowLoading != 'boolean' || false == options.isShowLoading) {
    options.isShowLoading = false;
  }
  if (typeof options.loadType != 'number' || options.loadType <= 0) {
    options.loadType = 0;
  }
  
  // loading显示的文本
  if (
    typeof options.loadingText != 'string' && 
    typeof options.loadingText != 'number'
  ) {
    options.loadingText = '加载中...';
  }
  options.loadingText += '';

  // 加载loading显示时间, 如果为0的话, 意味着一直显示
  // 默认显示30秒自动小时
  if (typeof options.loadingDuration != 'number') {
    options.loadingDuration = 1000 * 30;
  }

  if (true == options.isShowLoading) {
    let loadOptions = {
      message: options.loadingText,
      forbidClick: true,
      duration: options.loadingDuration,
      className: 'my-custom-loading my-custom-loading-'+options.loadType
    };
    if (1 == options.loadType) {
      const loadList = [
        require('@/assets/images/loads/loading-fragment1.gif'),
        require('@/assets/images/loads/loading-fragment2.gif'),
        require('@/assets/images/loads/loading-fragment3.gif')
      ];
      const loadSelectedIndex = Math.floor(Math.random()*loadList.length + 1) - 1;
      loadOptions.icon = loadList[loadSelectedIndex];
      loadOptions.iconSize = 200;
    }
    Toast.loading(loadOptions);
  }
  return new Promise((r) => {
    getApiTokenString().then(tokenResults => {
      if ('' == tokenResults.apiToken) {
        r(tokenResults);
        return tokenResults;
      }
      const userInfo = getUserInfo();
      magento.customer.getApiDatas(apiName, Object.assign({}, {
        "apiName": apiName,
        "apiAction": apiAction,
        "apiToken": tokenResults.apiToken
      }, null == userInfo ? {} : userInfo, bodyData)).then(apiRes => {
        r(apiRes);
        return apiRes;
      }).catch((err) => {
        if (shouldRedirectToLogin(err)) {
          r(err);
          return err;
        }
        if (
          err &&
          typeof err === 'object' &&
          err.code === 500 &&
          typeof err.msg === 'string' &&
          err.msg.trim() !== ''
        ) {
          r({ code: 500, msg: err.msg });
          return { code: 500, msg: err.msg };
        }
        r({});
        return {};
      });
    });
  }).then(apiRes => {
      if (true == options.isShowLoading) {
        Toast.clear();
      }
      if (
        typeof apiRes != 'object' || null == apiRes
      ) {
        apiRes = {};
      }
      let code = getObjectValue(apiRes, 'code', -1);
      if (
        !/[\\-]{0,1}[0-9]{1,}$/.test((code+''))
      ) {
        code = -1;
      }
      let msg = getObjectValue(apiRes, 'msg', '');
      if (
        typeof msg != 'string' && typeof msg != 'number'
      ) {
        msg = '';
      }
      if (handleLoginRequiredResponse(apiRes)) {
        return Object.assign({}, apiRes, {
          code,
          msg,
          data: getObjectValue(apiRes, 'data', null),
        });
      }
      // 图片上传成功后统一提示内容检测说明（可通过 options.skipImageUploadAuditTip 跳过，改由业务最终成功时提示）
      if (
        apiName === 'upload' &&
        apiAction === 'uploadBase64Img' &&
        code == 0 &&
        options.skipImageUploadAuditTip !== true
      ) {
        setTimeout(() => {
          notifyImageUploadAuditTip();
        }, 80);
      }
      return Object.assign({}, apiRes, {
        code, msg, data: getObjectValue(apiRes, 'data', null)
      });
  });
}